import { useTranslation } from 'next-i18next';
import {
  FC, useCallback, useMemo,
} from 'react';
import { useForm, useFormState } from 'react-final-form';
import SkeletonBlock from 'src/common/components/SkeletonLoader/SkeletonBlock';
import { CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE } from 'src/common/constants';
import { getCaptainCopyListing } from 'src/common/helpers';
import { CaptainOptionValue } from 'src/common/types/CaptainOptions';
import { TripCartFormState } from 'src/listing/types';
import TripCartDropdown, { TripCartDropdownOption } from 'src/trip-cart-deprecated/components/TripCartDropdown';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';
import { captainValueEqualsInstabook, getMatchingInstabookAvailability } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext/getTripCartOptions';

type Values = `${CaptainOptionValue}`;

const TripCartCaptainDropdown: FC = () => {
  const [t] = useTranslation('listing-page');
  const { change } = useForm<TripCartFormState>();
  const { values } = useFormState<TripCartFormState>();
  const { instabookAvailabilities } = useTripCartContext();
  const { options } = useTripCartOptionsContext();

  const items = useMemo(() => {
    const only = options.withCaptain.length === 1;
    const list: TripCartDropdownOption<Values>[] = options.withCaptain.map((value) => {
      const text = getCaptainCopyListing(value, only);
      const selfCaptain = value === CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE;
      const duration = options.formDuration.find(({ durationFormatted }) => (
        durationFormatted === values.formDuration
      ));

      const instabook = values.isMultiDay
        ? undefined
        : getMatchingInstabookAvailability(
          {
            ...values,
            // override value to get any option, not only active one
            withCaptain: value,
          },
          instabookAvailabilities,
        );

      return {
        id: `${value}`,
        label: t(text),
        hasInstabook: instabook && duration?.isInstabook
         && captainValueEqualsInstabook(value, instabook),
        icon: selfCaptain ? 'no-captain' : 'with-captain',
      };
    });
    return list;
  }, [instabookAvailabilities, options.formDuration, options.withCaptain, t, values]);

  const selected = useMemo((): TripCartDropdownOption<Values> => (
    items.find((item) => Number(item.id) === values.withCaptain)
      ?? items[0]
  ), [items, values.withCaptain]);

  const onSelect = useCallback((option: TripCartDropdownOption<Values>) => {
    change('withCaptain', Number(option.id) as CaptainOptionValue);
  }, [change]);

  return (
    <SkeletonBlock loading={!items}>
      <TripCartDropdown
        id="withCaptain"
        options={items ?? []}
        onSelect={onSelect}
        selectedOption={selected}
        align="right"
        attentionField="withCaptain"
      />
    </SkeletonBlock>
  );
};

export default TripCartCaptainDropdown;
